// "Yeni Değerlendirme" — the mockup's review form, adapted to the owner's 5-criterion
// model. The QMS specialist picks an agent, attaches the recording, scores the five
// criteria and ticks any fatal mistakes; the sidebar shows the score updating live.
//
// The live figure is a PREVIEW (see livescore.ts). What gets stored is what the server
// computes from the same criteria table, and the success message quotes the server's
// number, not the preview's.

import { useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import type { AgentOption, Assignment, Meta } from '../types';
import { useAsync } from '../hooks';
import { BreakdownRow, Callout, CriticalBanner, Icon, Loading, Note, PageHead, Panel, ScoreHero } from '../ui';
import { audioRequired, liveScore, noteRequired } from '../livescore';
import { fileSize, score10, shortDateTime, toLocalInput } from '../format';
import { navigate } from '../App';
import ScoreForm from './ScoreForm';
import { AnsweredPill, outcomeLabel, recMissText, talkTime } from './Assignments';

function qualityLabel(final: number | null, hasCritical: boolean): string {
  if (hasCritical) return 'Kritik Hata';
  if (final === null) return 'Puanlanmadı';
  if (final >= 9) return 'Çok İyi';
  if (final >= 7) return 'İyi';
  return 'Geliştirilmeli';
}

export default function NewEvaluation({ meta, assignmentId = null }: {
  meta: Meta | null;
  /** An auto-assigned call (#/new/<id>, Task Board #163): its fields pre-fill the form and
   *  saving closes it. null = a hand-picked call. */
  assignmentId?: number | null;
}) {
  const agents = useAsync<AgentOption[]>(() => api.getAgents(), []);
  const assignment = useAsync<Assignment | null>(
    () => (assignmentId ? api.getAssignment(assignmentId) : Promise.resolve(null)),
    [assignmentId],
  );
  const asg = assignment.data;

  const [agentId, setAgentId] = useState('');
  // The called member's BackOffice user id — required (owner 2026-09-15). Kept as the raw
  // string the field holds; the server validates it (decimal, positive) and is the authority.
  const [playerUserId, setPlayerUserId] = useState('');
  // Empty on purpose — NOT prefilled with "now". QMS scoring is retrospective (a recorded
  // call from a prior day/week), so a "now" default silently filed the call under today's
  // period and dropped it out of the report week it actually belongs to. Required on both
  // sides (canSubmit below + parseCallAt on the server), so an empty start just forces the
  // specialist to enter the call's real time.
  const [callAt, setCallAt] = useState('');
  const [listKey, setListKey] = useState('');
  const [callTypeKey, setCallTypeKey] = useState('');
  const [notes, setNotes] = useState('');
  const [scores, setScores] = useState<Record<string, number | null>>({});
  const [criticals, setCriticals] = useState<string[]>([]);
  // Up to meta.audio.maxFiles recordings (owner 2026-09-10). Picking APPENDS, so a
  // specialist can add them one at a time from different folders — a fresh <input multiple>
  // replaces its own FileList on every pick, which would silently drop the earlier choice.
  const [audios, setAudios] = useState<File[]>([]);
  const [fileKey, setFileKey] = useState(0);
  const [audioError, setAudioError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<api.NewEvaluationResult | null>(null);

  // Pre-fill from the assignment once it lands. The specialist may still correct the call time
  // or the list after listening; the agent is fixed (the server refuses another one for it).
  useEffect(() => {
    if (!asg) return;
    setAgentId(String(asg.agentId));
    setPlayerUserId(String(asg.playerUserId));
    setCallAt(toLocalInput(asg.callAt));
    setListKey(asg.listKey ?? '');
    setCallTypeKey('');
  }, [asg]);
  const assignmentOpen = Boolean(asg && asg.status === 'open');
  // Recordings the getircall fetcher pre-staged for this assigned call. They are promoted into
  // the evaluation on submit, so they satisfy the "recording required" rule and the specialist
  // need not upload — they just listen and score.
  const staged = (assignmentOpen && asg?.stagedAudio) ? asg.stagedAudio : [];

  const criteria = meta?.criteria ?? [];
  const rules = meta?.criticalRules ?? [];
  const callLists = meta?.callLists ?? [];
  const callTypes = meta?.callTypes ?? [];
  // The picked list decides what the second field is. SLACK/FORM/TAKİP say only where the
  // request arrived, so the type is asked for; every other list IS its own call type and the
  // field turns read-only — the server derives the same value and ignores anything sent
  // (engine/call-list.js), so an editable box here would be a lie.
  const pickedList = callLists.find((l) => l.key === listKey) ?? null;
  const asksType = Boolean(pickedList?.asksType);
  const derivedType = pickedList && !pickedList.asksType ? pickedList.label : '';
  // Fallbacks only cover the first render before meta lands.
  const audioExts = meta?.audio?.extensions ?? ['.mp3', '.wav', '.m4a', '.ogg'];
  const audioMaxMb = meta?.audio?.maxMb ?? 60;
  const audioRequiredFlag = meta?.audio?.required ?? true;
  const audioMaxFiles = meta?.audio?.maxFiles ?? 3;
  const noteThreshold = meta?.notes?.requiredBelow ?? 10;
  const live = useMemo(() => liveScore(scores, criticals, criteria), [scores, criticals, criteria]);
  const criticalLabels = criticals
    .map((k) => rules.find((r) => r.key === k)?.label || k);
  // A recording is required on every call now (owner, 2026-09-07); the server enforces it.
  const needsAudio = audioRequired(audioRequiredFlag);
  // Anything short of a perfect score has to say WHY (owner, 2026-09-06) — a number with no
  // explanation is a grade, not coaching, and it leaves an objection nothing to answer.
  const needsNote = noteRequired(live, noteThreshold);

  // Decimal digits only — mirrors the server's parsePlayerUserId so an obvious mistype does not
  // round-trip to a 400. The server stays the authority (empty/overflow/etc. all refused there).
  const playerUserIdValid = /^[0-9]{1,15}$/.test(playerUserId.trim());

  const canSubmit = Boolean(agentId) && playerUserIdValid && Boolean(callAt)
    && Boolean(listKey) && (!asksType || Boolean(callTypeKey))
    && (!needsAudio || audios.length > 0 || staged.length > 0)
    && (!needsNote || Boolean(notes.trim()))
    && live.complete && !busy;

  /**
   * Add the picked files to the set. An over-cap pick is REFUSED whole rather than trimmed
   * to what fits — the same posture the server takes (engine/audio-set.js): keeping the
   * first N of what someone attached stores evidence nobody chose.
   */
  function addAudios(list: FileList | null) {
    const picked = Array.from(list ?? []);
    // Reset the input so re-picking the SAME file still fires onChange (a file removed from
    // the list below could otherwise never be added back).
    setFileKey((k) => k + 1);
    if (!picked.length) return;
    if (audios.length + picked.length > audioMaxFiles) {
      setAudioError(`En fazla ${audioMaxFiles} çağrı kaydı eklenebilir.`);
      return;
    }
    setAudioError('');
    setAudios((a) => [...a, ...picked]);
  }

  function removeAudio(index: number) {
    setAudioError('');
    setAudios((a) => a.filter((_, i) => i !== index));
  }

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.createEvaluation({
        agentId: Number(agentId),
        playerUserId: playerUserId.trim(),
        callAt, listKey, callTypeKey: asksType ? callTypeKey : '', notes,
        scores: Object.fromEntries(
          criteria.map((c) => [c.key, scores[c.key] as number]),
        ),
        criticals,
        audios,
        assignmentId: assignmentOpen ? asg!.id : null,
      });
      setDone(result);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Değerlendirme kaydedilemedi.');
    } finally {
      setBusy(false);
    }
  }

  function clearAnswers() {
    setScores({}); setCriticals([]);
  }

  function resetForNext() {
    setDone(null);
    setAgentId('');
    setPlayerUserId('');
    setCallAt('');
    setListKey(''); setCallTypeKey(''); setNotes('');
    setScores({}); setCriticals([]); setAudios([]); setAudioError(''); setFileKey((k) => k + 1);
  }

  if (!meta || (assignmentId && assignment.loading && !asg)) return <Loading />;

  if (done) {
    return (
      <>
        <PageHead icon="check_circle" title="Değerlendirme kaydedildi" subtitle="Agent panosuna ve raporlara işlendi." />
        <div className="review">
          <div className="review-side" style={{ maxWidth: 420 }}>
            <section className="panel">
              <ScoreHero score={done.finalScore} hasCritical={done.hasCritical} label={done.qualityLabel} />
              {done.hasCritical ? (
                <div style={{ padding: '0 18px 16px' }}>
                  <Callout tone="bad">
                    {`Kriter ortalaması ${score10(done.weighted)} olsa da kritik hata nedeniyle çağrı 0,0 aldı.`}
                  </Callout>
                </div>
              ) : null}
              <div className="panel-foot">
                {assignmentId ? (
                  <button className="btn pri full" type="button" onClick={() => navigate('assigned')}>
                    <Icon name="assignment" /><span>Atanan çağrılara dön</span>
                  </button>
                ) : (
                  <button className="btn pri full" type="button" onClick={resetForNext}>
                    <Icon name="add" /><span>Yeni değerlendirme gir</span>
                  </button>
                )}
                <button className="btn full" type="button" onClick={() => navigate(`evaluation/${done.id}`)}>
                  Kaydı görüntüle
                </button>
              </div>
            </section>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        icon="playlist_add_check"
        title="Çağrı Değerlendirmesi"
        subtitle="Çağrı kaydını yükleyin, beş kriteri puanlayın ve varsa kritik hataları işaretleyin."
      />

      {assignment.error ? <Note error>{assignment.error}</Note> : null}
      {asg ? (
        assignmentOpen ? (
          <Callout tone="accent" icon="assignment">
            <div>{`Atanan çağrı · ${outcomeLabel(asg)} · ${asg.agentName} · ${shortDateTime(asg.callAt)}${asg.listLabel ? ` · ${asg.listLabel}` : ''}`}</div>
            {/* The softphone's record of the call (migration 019) — absent on older builds. */}
            {asg.answered != null || asg.talkSec != null ? (
              <div className="row" style={{ gap: 6, marginTop: 4 }}>
                <AnsweredPill answered={asg.answered} />
                {asg.talkSec != null ? (
                  <span>{`Konuşma süresi: ${talkTime(asg.talkSec)}${asg.talkSource === 'estimated' ? ' (tahmini)' : ''}`}</span>
                ) : null}
              </div>
            ) : null}
          </Callout>
        ) : (
          <Callout tone="warn">Bu atanan çağrı zaten kapandı — kaydedilen değerlendirme atamaya bağlanmaz.</Callout>
        )
      ) : null}

      <div className="review">
        <div className="review-main">
          <Panel icon="mic" title="Çağrı bilgileri" hint="Agent, kayıt ve çağrı künyesi">
            <div className="formgrid">
              <div className="field">
                <label htmlFor="ag">Agent *</label>
                <select
                  id="ag" className="inp" value={agentId} disabled={assignmentOpen}
                  onChange={(e) => setAgentId(e.target.value)}
                >
                  <option value="">Seçin…</option>
                  {(agents.data ?? []).map((a) => (
                    <option key={a.id} value={a.id}>{a.displayName} · {a.brandKey}</option>
                  ))}
                </select>
                {agents.data && agents.data.length === 0
                  ? <span className="hint" style={{ color: 'var(--bad)' }}>Kayıtlı aktif agent yok.</span>
                  : null}
              </div>
              <div className="field">
                <label htmlFor="uid">Kullanıcı ID *</label>
                <input
                  id="uid" className="inp" inputMode="numeric" pattern="[0-9]*"
                  value={playerUserId}
                  onChange={(e) => setPlayerUserId(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="123456"
                />
                <span className="hint">Aranan üyenin BackOffice kullanıcı numarası.</span>
              </div>
              <div className="field">
                <label htmlFor="ca">Çağrı tarihi ve saati * <span>(İstanbul)</span></label>
                <input id="ca" className="inp" type="datetime-local" value={callAt} onChange={(e) => setCallAt(e.target.value)} />
                <span className="hint">Çağrının gerçekleştiği an — bugünün değil; dönem raporu buna göre belirlenir.</span>
              </div>
              <div className="field">
                <label htmlFor="ln">Liste *</label>
                <select
                  id="ln" className="inp" value={listKey}
                  onChange={(e) => { setListKey(e.target.value); setCallTypeKey(''); }}
                >
                  <option value="">Seçin…</option>
                  {callLists.map((l) => (
                    <option key={l.key} value={l.key}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="ct">Arama tipi{asksType ? ' *' : ''}</label>
                {asksType ? (
                  <select id="ct" className="inp" value={callTypeKey} onChange={(e) => setCallTypeKey(e.target.value)}>
                    <option value="">Seçin…</option>
                    {callTypes.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                ) : (
                  /* Disabled, not hidden: the specialist has to SEE what the call will be
                     filed as. Empty until a list is picked — a placeholder guess here would
                     be the free text this field stopped being. */
                  <input id="ct" className="inp" value={derivedType} readOnly disabled placeholder="Listeden gelir" />
                )}
                {asksType ? (
                  <span className="hint">SLACK / FORM / TAKİP için arama sebebi ayrıca seçilir.</span>
                ) : null}
              </div>
              <div className="field wide">
                <label htmlFor="au">Çağrı kaydı{needsAudio ? ' *' : ''}</label>
                {/* Recordings the bot already fetched from getircall for this call — shown
                    pre-attached and playable; the specialist listens here instead of uploading. */}
                {staged.length > 0 ? (
                  <div className="note" style={{ marginBottom: 8 }}>
                    <Icon name="graphic_eq" style={{ color: 'var(--accent)', fontSize: 16 }} />
                    <span>{` Kayıt otomatik getirildi (${staged.length}) — dinleyip puanlayın.`}</span>
                  </div>
                ) : null}
                {/* The fetcher tried this call and staged nothing — say why, so the specialist
                    knows to upload by hand rather than wait. */}
                {assignmentOpen && staged.length === 0 && asg?.recLastMiss ? (
                  <div className="note" style={{ marginBottom: 8 }}>
                    <Icon name="mic_off" style={{ color: 'var(--warn)', fontSize: 16 }} />
                    <span>{` ${recMissText(asg)}`}</span>
                  </div>
                ) : null}
                {staged.map((a) => (
                  <div className="filerow" key={`staged-${a.id}`}>
                    <Icon name="graphic_eq" style={{ color: 'var(--accent)', fontSize: 18 }} />
                    <audio controls preload="metadata" src={api.audioSrc(a.url)} style={{ height: 32, flex: 1 }}>
                      Tarayıcınız ses oynatmayı desteklemiyor.
                    </audio>
                    <span className="sz">{fileSize(a.bytes)}</span>
                  </div>
                ))}
                {/* `multiple`, and the picks APPEND (addAudios): one call is regularly split
                    across a transfer, a call-back or a recorder rollover, and the agent has
                    to be able to hear all of it before deciding whether to object. The
                    input is hidden once the set is full rather than left to fail. */}
                {audios.length < audioMaxFiles ? (
                  <input
                    key={fileKey}
                    id="au" className="inp" type="file" multiple
                    accept={`${audioExts.join(',')},audio/*`}
                    onChange={(e) => addAudios(e.target.files)}
                  />
                ) : null}
                {audios.map((f, i) => (
                  <div className="filerow" key={`${f.name}-${f.size}-${i}`}>
                    <Icon name="graphic_eq" style={{ color: 'var(--accent)', fontSize: 18 }} />
                    <span className="nm">{f.name}</span>
                    <span className="sz">{fileSize(f.size)}</span>
                    <button
                      className="btn ghost sm" type="button"
                      title="Kaydı çıkar" onClick={() => removeAudio(i)}
                    >
                      <Icon name="close" />
                    </button>
                  </div>
                ))}
                {audioError ? <span className="hint" style={{ color: 'var(--bad)' }}>{audioError}</span> : null}
                {/* Every number comes from /api/meta: the hint used to name four of the nine
                    accepted formats and a hard-coded 60 MB that drifts from AUDIO_MAX_MB.
                    Built as ONE template string, not JSX interpolation, so it stays a single
                    text node for the EN overlay's phrase regex (Rule 10). The size is PER
                    FILE, which is why it is said that way. */}
                <span className="hint">
                  {`${audioExts.map((x) => x.replace('.', '')).join(', ')} · dosya başına en fazla ${audioMaxMb} MB`}
                </span>
                <span className="hint">
                  {`Çağrı kaydı her çağrıda zorunlu · en fazla ${audioMaxFiles} kayıt eklenebilir.`}
                </span>
              </div>
            </div>
          </Panel>

          <ScoreForm
            criteria={criteria}
            rules={rules}
            scores={scores}
            criticals={criticals}
            onScore={(key, v) => setScores((s) => ({ ...s, [key]: v }))}
            onCritical={(key, on) => setCriticals((c) => (on ? [...c, key] : c.filter((x) => x !== key)))}
          />

          {live.hasCritical ? <CriticalBanner names={criticalLabels} /> : null}

          <Panel
            icon="edit_note"
            title={needsNote ? "QMS notu ve agent'a geri bildirim *" : "QMS notu ve agent'a geri bildirim"}
            hint="Agent panosunda görünür"
          >
            <div className="panel-body">
              <textarea id="nt" className="inp" style={{ minHeight: 96 }} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Agent'a iletilecek gözlemler…" />
              {/* Built as ONE template string so the EN overlay sees a single text node
                  (the threshold rides in it — see i18n.ts translatePhrase). */}
              <span className="hint">
                {needsNote
                  ? `Bu puan için zorunlu: ${noteThreshold} puanın altındaki her çağrı açıklama ister.`
                  : `Yalnızca ${noteThreshold} puanın altındaki çağrılarda zorunlu.`}
              </span>
            </div>
          </Panel>
        </div>

        <div className="review-side">
          <div className="sticky">
            <section className="panel">
              <ScoreHero
                score={live.final} hasCritical={live.hasCritical}
                label={qualityLabel(live.final, live.hasCritical)}
              />
              <div className="brk">
                {criteria.map((c) => (
                  <BreakdownRow key={c.key} label={c.label} weight={c.weight} value={scores[c.key] ?? null} />
                ))}
                {live.hasCritical ? (
                  <div className="brk-row kv">
                    <span className="nm">Kriter ortalaması</span>
                    <span className="val" style={{ color: 'var(--faint)' }}>{score10(live.weighted)}</span>
                  </div>
                ) : null}
              </div>
              <div className="panel-foot">
                {error ? <Note error>{error}</Note> : null}
                <button className="btn pri full" type="button" disabled={!canSubmit} onClick={submit}>
                  <Icon name="check_circle" /><span>{busy ? 'Kaydediliyor…' : 'Değerlendirmeyi kaydet'}</span>
                </button>
                <button className="btn ghost full" type="button" disabled={busy} onClick={clearAnswers}>
                  Tüm cevapları temizle
                </button>
                {!live.complete ? (
                  <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                    Kaydetmek için beş kriterin tamamı puanlanmalı.
                  </span>
                ) : null}
                {needsAudio && audios.length === 0 && staged.length === 0 ? (
                  <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                    Çağrı kaydı zorunlu.
                  </span>
                ) : null}
                {!playerUserIdValid ? (
                  <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                    Kullanıcı ID girilmeli.
                  </span>
                ) : null}
                {!listKey ? (
                  <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                    Liste seçilmeli.
                  </span>
                ) : null}
                {needsNote && !notes.trim() ? (
                  <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                    Bu puanda QMS notu zorunlu.
                  </span>
                ) : null}
                {asksType && !callTypeKey ? (
                  <span className="faint" style={{ fontSize: 11.5, textAlign: 'center' }}>
                    Arama tipi seçilmeli.
                  </span>
                ) : null}
              </div>
            </section>
            <Callout center>
              Skor kriter ağırlıklarına göre otomatik hesaplanır ve sunucuda yeniden doğrulanır.
              Kaydedince agent panosuna ve dönem raporuna işlenir.
            </Callout>
          </div>
        </div>
      </div>
    </>
  );
}
